import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Send, Fish, Mail, MessageSquare, User, Image, ChevronDown } from 'lucide-react';

const Contact = () => {
  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    setErrorMessage('');

    const formElement = e.target;
    const formData = new FormData(formElement);

    // Client-side validation for files
    const files = formData.getAll('images');
    let totalSize = 0;
    let fileCount = 0;
    const allowedExtensions = ['pdf', 'png', 'jpg', 'jpeg', 'gif', 'webp'];

    for (const file of files) {
      if (file instanceof File && file.name) {
        fileCount++;
        
        // Check file extension
        const ext = file.name.split('.').pop().toLowerCase();
        if (!allowedExtensions.includes(ext)) {
          setStatus('error');
          setErrorMessage(`Filtypen .${ext} er ikke tilladt. Tilladte typer er: PDF, PNG, JPEG, GIF, WEBP.`);
          return;
        }

        // Check individual file size (max 10 MB)
        if (file.size > 10 * 1024 * 1024) {
          setStatus('error');
          setErrorMessage(`Filen "${file.name}" er for stor. En enkelt fil må højst være 10 MB.`);
          return;
        }

        totalSize += file.size;
      }
    }

    if (fileCount > 5) {
      setStatus('error');
      setErrorMessage('Du kan højst vedhæfte 5 filer.');
      return;
    }

    if (totalSize > 20 * 1024 * 1024) {
      setStatus('error');
      setErrorMessage('Den samlede filstørrelse må ikke overstige 20 MB.');
      return;
    }

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        body: formData, // Send FormData directly for multipart/form-data
      });

      if (response.ok) {
        const resData = await response.json();
        if (resData.status === 'ok') {
          setStatus('success');
          formElement.reset();
        } else {
          setStatus('error');
          setErrorMessage(resData.message || 'Der skete en fejl under indsendelsen.');
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        setStatus('error');
        setErrorMessage(errorData.message || `Serveren svarede med fejlstatus: ${response.status}`);
      }
    } catch (error) {
      console.error(error);
      setStatus('error');
      setErrorMessage('Der kunne ikke oprettes forbindelse til serveren. Prøv venligst igen senere.');
    }
  };

  return (
    <>
      <Helmet>
        <title>Kontakt mig | LimfjordsOdderen</title>
        <meta name="description" content="Har du et godt fiske-tip eller vil du bare sige hej? Send mig en besked her." />
      </Helmet>

      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div>
              <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-8">
                Send mig en sild (eller en besked)
              </h1>
              <p className="text-xl text-slate-600 mb-12">
                Jeg er altid klar på en snak, især hvis den handler om de bedste fiskepladser i Limfjorden. Udfyld formularen, så svarer jeg så hurtigt jeg kan (når jeg ikke lige er ude og dykke).
              </p>

              <div className="space-y-8">
                <div className="flex items-start gap-6">
                  <div className="bg-blue-100 p-4 rounded-2xl text-blue-600">
                    <Mail className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900">E-mail</h3>
                    <p className="text-slate-600">sild@limfjordsodderen.dk</p>
                  </div>
                </div>
                <div className="flex items-start gap-6">
                  <div className="bg-blue-100 p-4 rounded-2xl text-blue-600">
                    <Fish className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900">Hovedkvarter</h3>
                    <p className="text-slate-600">En stor sten under Aggersundbroen</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 p-8 md:p-12 rounded-3xl shadow-inner">
              {status === 'success' ? (
                <div className="text-center py-12">
                  <div className="bg-green-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 text-green-600">
                    <Send className="h-10 w-10" />
                  </div>
                  <h2 className="text-2xl font-bold text-slate-900 mb-4">Beskeden er sendt!</h2>
                  <p className="text-slate-600 mb-8">Tak for din hilsen. Jeg tjekker min indbakke næste gang jeg kommer på land.</p>
                  <button 
                    onClick={() => setStatus('idle')}
                    className="text-blue-600 font-bold hover:underline"
                  >
                    Send en anden besked
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Honeypot field - HIDDEN FROM HUMANS */}
                  <div className="sr-only absolute -z-10 opacity-0">
                    <label htmlFor="website_url">Website URL</label>
                    <input type="text" id="website_url" name="website_url" tabIndex="-1" autoComplete="off" />
                  </div>

                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                      <User className="h-4 w-4" /> Dit navn
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-4 py-3 rounded-xl border border-blue-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all bg-white"
                      placeholder="Fiske-Finn"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                      <Mail className="h-4 w-4" /> Din e-mail
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-3 rounded-xl border border-blue-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all bg-white"
                      placeholder="finn@fisk.dk"
                    />
                  </div>

                  <div>
                    <label htmlFor="category" className="block text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                      <ChevronDown className="h-4 w-4" /> Vælg kategori
                    </label>
                    <select
                      id="category"
                      name="category"
                      required
                      defaultValue=""
                      className="w-full px-4 py-3 rounded-xl border border-blue-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all bg-white appearance-none cursor-pointer"
                    >
                      <option value="" disabled>Vælg en mulighed...</option>
                      <option value="Dåsetun">Dåsetun</option>
                      <option value="Lune dadler">Lune dadler</option>
                      <option value="Hesteparyk">Hesteparyk</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                      <MessageSquare className="h-4 w-4" /> Din besked
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows="5"
                      className="w-full px-4 py-3 rounded-xl border border-blue-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all bg-white resize-none"
                      placeholder="Hej LimfjordsOdder! Jeg har fundet en kæmpe stime sild ved..."
                    ></textarea>
                  </div>

                  <div>
                    <label htmlFor="images" className="block text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                      <Image className="h-4 w-4" /> Vedhæft filer (maks. 5)
                    </label>
                    <input
                      type="file"
                      id="images"
                      name="images"
                      multiple
                      accept=".pdf,.png,.jpg,.jpeg,.gif,.webp"
                      onChange={(e) => {
                        const files = Array.from(e.target.files);
                        let totalSize = 0;
                        const allowedExtensions = ['pdf', 'png', 'jpg', 'jpeg', 'gif', 'webp'];
                        
                        if (files.length > 5) {
                          alert('Du kan højst vælge 5 filer.');
                          e.target.value = '';
                          return;
                        }

                        for (const file of files) {
                          const ext = file.name.split('.').pop().toLowerCase();
                          if (!allowedExtensions.includes(ext)) {
                            alert(`Filtypen .${ext} er ikke tilladt. Tilladte typer er: PDF, PNG, JPEG, GIF, WEBP.`);
                            e.target.value = '';
                            return;
                          }
                          if (file.size > 10 * 1024 * 1024) {
                            alert(`Filen "${file.name}" er for stor. Maks. 10 MB per fil.`);
                            e.target.value = '';
                            return;
                          }
                          totalSize += file.size;
                        }

                        if (totalSize > 20 * 1024 * 1024) {
                          alert('Den samlede filstørrelse må ikke overstige 20 MB.');
                          e.target.value = '';
                        }
                      }}
                      className="w-full px-4 py-3 rounded-xl border border-blue-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all bg-white text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={status === 'loading'}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 rounded-xl transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {status === 'loading' ? 'Sender...' : (
                      <>
                        Send besked <Send className="h-5 w-5" />
                      </>
                    )}
                  </button>

                  {status === 'error' && (
                    <p className="text-red-500 text-sm text-center font-medium">
                      {errorMessage || 'Der skete en fejl. Prøv venligst igen senere.'}
                    </p>
                  )}
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Contact;
